import { useState, useRef, useEffect, useCallback } from 'react';
import { playNotificationSound } from '../../lib/notificationSound';
import { NavLink, useNavigate } from 'react-router-dom';
import { LuBell, LuX, LuMenu } from 'react-icons/lu';
import { LiaCheckDoubleSolid } from 'react-icons/lia';
import styles from './TransporterLayout.module.css';
import BellIcon from '../../icons/BellIcon';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from '../../lib/directus';

function formatNotifTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr)
    .toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    .replace(',', ' at');
}

export default function TransporterLayout({ children }) {
  const navigate = useNavigate();
  const [bellOpen, setBellOpen]       = useState(false);
  const [menuOpen, setMenuOpen]       = useState(false);
  const [navOpen, setNavOpen]         = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const bellRef        = useRef(null);
  const avatarRef      = useRef(null);
  const seenIdsRef     = useRef(null); // null = first load (no sound)

  const loadNotifications = useCallback(() => {
    fetchNotifications()
      .then(data => {
        const mapped = data.map(n => ({
          id:      n.id,
          message: n.message || n.subject || '',
          time:    n.timestamp || n.date_created || '',
          read:    n.status === 'archived',
        }));

        if (seenIdsRef.current !== null) {
          const hasNew = mapped.some(n => !n.read && !seenIdsRef.current.has(n.id));
          if (hasNew) playNotificationSound();
        }
        seenIdsRef.current = new Set(mapped.map(n => n.id));
        setNotifications(mapped);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30_000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    function handleMousedown(e) {
      if (bellRef.current && !bellRef.current.contains(e.target))     setBellOpen(false);
      if (avatarRef.current && !avatarRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleMousedown);
    return () => document.removeEventListener('mousedown', handleMousedown);
  }, []);

  async function markAllRead() {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try { await markAllNotificationsRead(); } catch (err) { console.error(err); }
  }

  async function handleNotifClick(n) {
    if (n.read) return;
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    try { await markNotificationRead(n.id); } catch (err) { console.error(err); }
  }

  async function handleDeleteNotif(e, id) {
    e.stopPropagation();
    setNotifications(prev => prev.filter(x => x.id !== id));
    try { await deleteNotification(id); } catch (err) { console.error(err); }
  }

  function handleLogout() {
    localStorage.clear();
    window.location.replace('/login');
  }

  const hasUnread = notifications.some(n => !n.read);

  return (
    <div>
      {/* Top border */}
      <div className={styles.topBorder} />

      {/* Header */}
      <header className={styles.header}>
        {/* Logo */}
        <img src="/driftwood-logo.png" alt="Driftwood Agri" style={{ height: 70 }} />

        {/* Center nav (desktop) */}
        <nav className={styles.nav}>
          <NavLink
            to="/dashboard"
            className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/loads"
            className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
          >
            Loads
          </NavLink>
          <NavLink
            to="/my-bids"
            className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
          >
            My bids
          </NavLink>
          <NavLink
            to="/trips"
            className={({ isActive }) => isActive ? styles.navLinkActive : styles.navLink}
          >
            Trips
          </NavLink>
        </nav>

        {/* Right actions */}
        <div className={styles.headerActions}>
          {/* Hamburger (mobile only) */}
          <button
            className={styles.hamburger}
            onClick={() => setNavOpen(o => !o)}
            aria-label="Open navigation"
          >
            {navOpen ? <LuX size={22} /> : <LuMenu size={22} />}
          </button>
          {/* Bell */}
          <div ref={bellRef} style={{ position: 'relative' }}>
            <button
              className={styles.bellBtn}
              onClick={() => { setBellOpen(o => !o); setMenuOpen(false); }}
              aria-label="Notifications"
            >
              <BellIcon />
              {hasUnread && <span className={styles.bellDot} />}
            </button>

            {bellOpen && (
              <div className={styles.notificationsPanel}>
                <div className={styles.notifHeader}>
                  <p className={styles.notifTitle}>Notifications</p>
                  <button className={styles.notifClose} onClick={() => setBellOpen(false)}>
                    <LuX size={18} />
                  </button>
                </div>
                <div className={styles.markReadRow}>
                  <button className={styles.markRead} onClick={markAllRead}>
                    <LiaCheckDoubleSolid size={16} style={{ marginRight: 4 }} />
                    Mark all as read
                  </button>
                </div>
                {notifications.length === 0 && (
                  <div className={styles.notifEmpty}>No notifications</div>
                )}
                <div className={styles.notifList}>
                  {notifications.map(n => (
                    <div
                      key={n.id}
                      className={styles.notifItem}
                      onClick={() => handleNotifClick(n)}
                      style={{ cursor: n.read ? 'default' : 'pointer' }}
                    >
                      <div className={styles.notifIconWrap}>
                        <LuBell size={16} className={n.read ? styles.notifIconRead : styles.notifIconUnread} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <p className={n.read ? styles.notifTextRead : styles.notifTextUnread}>{n.message}</p>
                        <p className={styles.notifTime}>{formatNotifTime(n.time)}</p>
                      </div>
                      {!n.read && <span className={styles.notifDot} />}
                      <button className={styles.notifDeleteBtn} onClick={(e) => handleDeleteNotif(e, n.id)}>
                        <LuX size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Avatar */}
          <div className={styles.avatarWrapper} ref={avatarRef}>
            <img
              src="/avatar-holder-image.jpg"
              alt="User avatar"
              className={styles.avatar}
              onClick={() => { setMenuOpen(o => !o); setBellOpen(false); }}
            />
            {menuOpen && (
              <div className={styles.avatarMenu}>
                <button
                  className={styles.avatarMenuItem}
                  onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                >
                  Profile
                </button>
                <button
                  className={styles.avatarMenuItem}
                  onClick={() => { setMenuOpen(false); setLogoutModal(true); }}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile nav overlay */}
      {navOpen && (
        <div className={styles.mobileNav} onClick={() => setNavOpen(false)}>
          <NavLink to="/dashboard" className={({ isActive }) => isActive ? styles.mobileNavLinkActive : styles.mobileNavLink} onClick={() => setNavOpen(false)}>Dashboard</NavLink>
          <NavLink to="/loads"     className={({ isActive }) => isActive ? styles.mobileNavLinkActive : styles.mobileNavLink} onClick={() => setNavOpen(false)}>Loads</NavLink>
          <NavLink to="/my-bids"   className={({ isActive }) => isActive ? styles.mobileNavLinkActive : styles.mobileNavLink} onClick={() => setNavOpen(false)}>My bids</NavLink>
          <NavLink to="/trips"     className={({ isActive }) => isActive ? styles.mobileNavLinkActive : styles.mobileNavLink} onClick={() => setNavOpen(false)}>Trips</NavLink>
        </div>
      )}

      {/* Content */}
      <main className={styles.content}>
        {children}
      </main>

      {/* Logout modal */}
      {logoutModal && (
        <div className={styles.modalOverlay} onClick={() => setLogoutModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setLogoutModal(false)}>
              <LuX size={18} />
            </button>
            <p className={styles.modalMessage}>Are you sure you want to log out?</p>
            <div className={styles.modalActions}>
              <button className={styles.btnCancel} onClick={() => setLogoutModal(false)}>Cancel</button>
              <button className={styles.btnLogout} onClick={handleLogout}>Log out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
